/* eslint-disable max-lines */
import type { ViewFieldsFragment } from '@salescore/client-api'
import { type ProgressType, useMe } from '@salescore/client-common'
import {
  CORE_CONSTANT,
  getRecordNodeParents,
  type SheetCell,
  type ViewQueryRecordNode,
  type ViewQueryRecordNodeWithParent,
} from '@salescore/core'
import { organizationHasFeature } from '@salescore/frontend-common'
import { Popover } from 'antd'
import { Array, Record } from 'effect'
import type { CSSProperties, ReactElement } from 'react'
import { useLongPress } from 'react-use'

import type {
  PivotColumn,
  PivotRowColumn,
  PivotSystemColumn,
  RSheetColumn,
  RSheetRecordNode,
} from '../../../domain/column.ts'
import type { RSheetsCursorPosition } from '../../../domain/cursor/cursorTypes.ts'
import type { KpiPivotTableContext } from '../../../recoil/context.ts'
import { useCursorMutation } from '../../../recoil/cursor.ts'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms.ts'
import { openContextMenu, toggleEditMode } from '../../../rsheet/components/body/cell/cellAction.ts'
import { KpiCell } from '../../../rsheet/components/body/cell/kpi/KpiCell.tsx'
import { MINIMUM_WIDTH } from '../../../rsheet/components/body/const.tsx'
import type { SetMainCursorByRecordIdArguments } from '../../../rsheet/hooks/useSetMainCursorByRecordId.ts'
import { RSheetsStyle } from '../../../rsheet/util/RSheetsStyle.ts'

export const KpiPivotValueCell = ({
  sheetCell,
  rootRecordNode,
  column,
  rowIndex,
  columnIndex,
  isNewRecord,
  isFieldChanged,
  overrideValue,
  rowHeight,
  context,
  columns,
  setMainCursorByRecordId,
  view,
  setDragging,
  expandOnDrag,
  progress,
  ability,
}: {
  sheetCell: SheetCell
  rootRecordNode: RSheetRecordNode
  column: PivotColumn<ViewQueryRecordNode>
  rowIndex: number
  columnIndex: number
  isNewRecord: boolean
  isFieldChanged: boolean
  overrideValue?: unknown
  rowHeight: number
  context: KpiPivotTableContext
  columns: Array<
    PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  >
  setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
  view: ViewFieldsFragment
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  progress: ProgressType
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const { innerRowIndexStart, height, recordNode, value } = sheetCell
  const innerRowIndex = innerRowIndexStart
  const { width } = column

  const kpiStyle: CSSProperties = {
    // KPIセルのとき、横の罫線は表示しない
    borderBottom: `1px ${RSheetsStyle.color.border} solid`,
    borderRight: column.kpiBorderRight ? `1px ${RSheetsStyle.color.borderBlack} solid` : undefined,
  }
  const defaultStyle: CSSProperties = {
    width,
    height: height * rowHeight,
    position: 'relative',
    overflow: undefined,
    transition: RSheetsStyle.cellTransition,
    ...kpiStyle,
  }

  if (recordNode === undefined) {
    return (
      <KpiPivotValueEmptyCell
        rowIndex={rowIndex}
        columnIndex={columnIndex}
        innerRowIndex={innerRowIndexStart}
        defaultStyle={defaultStyle}
        setDragging={setDragging}
        expandOnDrag={expandOnDrag}
        ability={ability}
      />
    )
  }

  // KPI表示の時、合計・小計の考慮
  //
  // KPIピボット表示時、列に並べ替えが指定されているとツリーの形式が変わる（子レコードが存在しなくなる）
  // そのため、ルートノードに子レコードが存在するかどうかによって、小計・合計セルの判定を分けている
  const hasNoChildrenRecord = rootRecordNode.children.length === 0
  const totalStringNodes = getTotalStringNodes({ recordNode, hasNoChildrenRecord, column })
  const isSubTotalOrTotal = totalStringNodes.some(Boolean)
  const isTotal = isSubTotalOrTotal && totalStringNodes.length === 1
  const isSubTotal = isSubTotalOrTotal && totalStringNodes.length > 1
  const isTotalChild = totalStringNodes.filter(Boolean).length > 1 && totalStringNodes.at(-1) === true

  return (
    <RecordCell
      recordNode={recordNode}
      column={column}
      context={context}
      rowIndex={rowIndex}
      columnIndex={columnIndex}
      innerRowIndex={innerRowIndex}
      rootRecordNode={rootRecordNode}
      columns={columns}
      setMainCursorByRecordId={setMainCursorByRecordId}
      view={view}
      isFieldChanged={isFieldChanged}
      isNewRecord={isNewRecord}
      overrideValue={overrideValue}
      height={height}
      value={value}
      defaultStyle={defaultStyle}
      isSubTotal={isSubTotal}
      isTotal={isTotal}
      isTotalChild={isTotalChild}
      setDragging={setDragging}
      expandOnDrag={expandOnDrag}
      progress={progress}
    />
  )
}

const RecordCell = ({
  recordNode,
  column,
  context,
  rowIndex,
  columnIndex,
  innerRowIndex,
  rootRecordNode,
  columns,
  setMainCursorByRecordId,
  view,
  isFieldChanged,
  isNewRecord,
  overrideValue,
  height,
  value,
  defaultStyle,
  isSubTotal,
  isTotal,
  isTotalChild,
  setDragging,
  expandOnDrag,
  progress,
}: {
  recordNode: RSheetRecordNode
  column: PivotColumn<ViewQueryRecordNode>
  context: KpiPivotTableContext
  rowIndex: number
  columnIndex: number
  innerRowIndex: number
  rootRecordNode: RSheetRecordNode
  columns: Array<RSheetColumn<RSheetRecordNode>>
  setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
  view: ViewFieldsFragment
  isFieldChanged: boolean
  isNewRecord: boolean
  overrideValue?: unknown
  height: number
  value: unknown
  defaultStyle: CSSProperties
  isSubTotal: boolean
  isTotal: boolean
  isTotalChild: boolean
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  progress: ProgressType
}): ReactElement => {
  const { organization } = useMe()

  const changed = isFieldChanged || isNewRecord
  const currentValue = changed ? overrideValue : value

  const kpiRelatedStyle = getStyleByKpi({ column, isSubTotal, isTotal, isTotalChild })

  const mobileContextMenuEnabled = organizationHasFeature(organization, 'enable_mobile_context_menu')
  const longPressEventHandler = useLongPress(
    (event) => {
      if (!('touches' in event) || event.touches[0] === undefined) {
        return
      }
      const contextMenuPosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      }
      openContextMenu({
        rootRecordNode,
        columnIndex,
        innerRowIndex,
        contextMenuPosition,
        disabled: false,
        setMainCursorByRecordId,
      })
    },
    {
      isPreventDefault: false,
      delay: 300,
    },
  )

  const boxStyle: CSSProperties = {
    ...defaultStyle,
    padding: '10px 10px',
    ...kpiRelatedStyle,
    fontSize: 14,
    ...(height === 1
      ? {
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }
      : {}),
  }

  return (
    <div
      role="cell"
      key={column.key}
      data-e2e={`rsheet-cell-${rowIndex}-${columnIndex}-${innerRowIndex}`}
      style={boxStyle}
      {...(mobileContextMenuEnabled ? longPressEventHandler : {})}
      onContextMenu={(event) => {
        const contextMenuPosition = {
          x: event.clientX,
          y: event.clientY,
        }
        openContextMenu({
          rootRecordNode,
          columnIndex,
          innerRowIndex,
          contextMenuPosition,
          disabled: false,
          setMainCursorByRecordId,
        })
        event.preventDefault()
        event.stopPropagation()
      }}
      onPointerEnter={() => {
        expandOnDrag({ rowIndex, columnIndex, innerRowIndex })
      }}
      onPointerDown={(event) => {
        setDragging() // event.stopPropagation()などによりsetDraggingが実行されなくなると、ドラッグによるセルの範囲選択が動作しなくなる。
        // ドラッグによるセルの範囲選択を実現するためには、クリック時にメインカーソルを更新する必要がある。
        // クリックを離す時に発火するonClickだと遅すぎるので、onPointerDownで処理する。
        if (event.shiftKey && rootRecordNode.id !== undefined) {
          setMainCursorByRecordId({
            position: {
              recordId: rootRecordNode.id,
              columnIndex,
              innerRowIndex,
            },
            option: {
              expand: true,
            },
          })
          return
        }

        toggleEditMode({ rootRecordNode, columnIndex, innerRowIndex, disabled: false, setMainCursorByRecordId })
      }}
    >
      <CellBody
        column={column}
        value={currentValue}
        recordNode={recordNode}
        height={height}
        context={context}
        columns={columns}
        view={view}
        progress={progress}
      />
    </div>
  )
}

const getTotalStringNodes = ({
  recordNode,
  column,
  hasNoChildrenRecord,
}: {
  recordNode: ViewQueryRecordNodeWithParent
  column: PivotColumn<ViewQueryRecordNode>
  hasNoChildrenRecord: boolean
}): boolean[] => {
  if (hasNoChildrenRecord) {
    const dimensionKeys = Array.range(1, column.index).map((index) => `dimension${index}`)
    return dimensionKeys.map((key) => recordNode.attributes[key] === CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING)
  }

  const parents = getRecordNodeParents(recordNode)
  return parents.map((x) => Record.values(x.attributes).includes(CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING))
}

const getStyleByKpi = ({
  isTotal,
  isSubTotal,
  isTotalChild,
  column,
}: {
  isTotal: boolean
  isSubTotal: boolean
  isTotalChild: boolean
  column: PivotColumn<ViewQueryRecordNode>
}): CSSProperties => {
  const getBackgroundColor = (): string | undefined => {
    if (isTotal || isTotalChild) {
      return RSheetsStyle.color.kpiTotalCell
    }
    if (isSubTotal) {
      return RSheetsStyle.color.kpiSubTotalCell
    }
    if (column.sortState !== undefined) {
      return RSheetsStyle.color.sortedCell
    }
    return RSheetsStyle.color.kpiCell
    // typeがkpiでない = 行軸の列ではなく、このパターンは別で処理しているはず
  }
  return {
    backgroundColor: getBackgroundColor(),
    padding: 0,
  }
}

const KpiPivotValueEmptyCell = ({
  rowIndex,
  columnIndex,
  innerRowIndex,
  defaultStyle,
  setDragging,
  expandOnDrag,
  ability,
}: {
  rowIndex: number
  columnIndex: number
  innerRowIndex: number
  defaultStyle: CSSProperties
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const { expand, setContextMenuPosition } = useCursorMutation(ability)

  return (
    <div
      style={{
        ...defaultStyle,
        fontSize: 14,
        padding: '10px 10px',
        // backgroundColor: isRangeSelected ? 'rgba(14, 101, 235, 0.1)' : isEditing ? '#fff' : 'inherit',
        overflow: 'hidden',
        // boxShadow: isEditing ? '1px 4px 7px 2px rgb(60 64 67 / 15%)' : '', // 表示用のrelativeなdivでは下方向に表示されないが、input側のabsoluteの方で下方向が表示される
        // zIndex: isEditing ? 1 : 0,
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        transition: RSheetsStyle.cellTransition,
        textAlign: 'center',
        color: RSheetsStyle.color.gray,
      }}
      className="bg-gray-50"
      onPointerEnter={() => {
        expandOnDrag({ rowIndex, columnIndex, innerRowIndex })
      }}
      onPointerDown={(event) => {
        setDragging() // event.stopPropagation()などによりsetDraggingが実行されなくなると、ドラッグによるセルの範囲選択が動作しなくなる。
        // ドラッグによるセルの範囲選択を実現するためには、クリック時にメインカーソルを更新する必要がある。
        // クリックを離す時に発火するonClickだと遅すぎるので、onPointerDownで処理する。
        if (event.shiftKey) {
          expand({
            rowIndex,
            columnIndex,
            innerRowIndex,
          })
        }
      }}
      onContextMenu={(e) => {
        setContextMenuPosition(
          {
            columnIndex,
            rowIndex,
            innerRowIndex,
          },
          {
            x: e.clientX,
            y: e.clientY,
          },
        )
        e.preventDefault()
        e.stopPropagation()
      }}
    />
  )
}

const CellBody = ({
  column,
  context,
  height,
  recordNode,
  value,
  columns,
  view,
  progress,
}: {
  column: PivotColumn<ViewQueryRecordNode>
  value: unknown
  recordNode: ViewQueryRecordNodeWithParent
  height: number
  context: KpiPivotTableContext
  columns: Array<RSheetColumn<RSheetRecordNode>>
  view: ViewFieldsFragment
  progress: ProgressType
}): ReactElement => {
  if (column.width === MINIMUM_WIDTH) {
    return <></>
  }
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: 手抜き
  if (value === CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING) {
    return <StringCell value={`小計`} height={height} column={column} recordNode={recordNode} />
  }
  if (value === CORE_CONSTANT.KPI_PIVOT_NULL_STRING) {
    return <StringCell value={'-'} height={height} column={column} recordNode={recordNode} />
  }

  return (
    <KpiCell
      recordNode={recordNode}
      context={context}
      column={column}
      columns={columns}
      view={view}
      progress={progress}
    />
  )
}

const StringCell = ({
  value,
  height,
  column,
  recordNode,
}: {
  value: unknown
  height: number
  column: PivotColumn<ViewQueryRecordNode>
  recordNode: ViewQueryRecordNodeWithParent
}): ReactElement => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const label = column.label(recordNode) as string // 2023/08 StringCellだがラベル値があるのは、現状はsalescore_users_with_groupのみ
  const textLineSize = Math.floor(height * 1.82)
  const lineClampStyle: CSSProperties = {
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: textLineSize,
    display: '-webkit-box',
  }
  const stringValue =
    typeof value === 'string'
      ? value
      : // eslint-disable-next-line sonarjs/no-nested-conditional
        typeof value === 'number'
        ? `${value}`
        : // eslint-disable-next-line sonarjs/no-nested-conditional
          value === undefined || value === null
          ? ``
          : JSON.stringify(value)
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const text = label ?? stringValue
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: ここでpopoverしてしまうとpadding分を考慮できずhoverの判定が微妙になる。構造を変える必要がある。
  return (
    <Popover content={<pre style={{ width: 400 }}>{text}</pre>} placement="bottomLeft">
      {
        // 変則的だが、1行のときはspanで親要素divのtextOverflowによって三点リーダを表示し、1行より大きい場合はwebkit-line-clampを使って表示
        height === 1 ? <span>{text}</span> : <p style={lineClampStyle}>{text}</p>
      }
    </Popover>
  )
}
